import { Box, Chip, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { monospaceStyle } from "../../theme";
import { CategoryDetail } from "../../types/categoryDetail";
import { ValueChain } from "../../types/valueChain";
import { UNLABELLED_CATEGORY } from "../CategoryDetail/CategoryHead";
import EntityLink from "../Detail/EntityLink";
import SourceTimeline from "../Detail/SourceTimeline";
import { codeWords } from "../Detail/format";
import { ChainCardRef } from "../ValueChain/valueChainState";
import ChainPlace from "./ChainPlace";
import { chainMakers } from "./chainSections";
import {
  ON_RECORD,
  cardOfCategory,
  chainPlaceLine,
  usePageChain,
} from "./kindChain";

interface KindHeadProps {
  category: CategoryDetail;
  // The category id the kind was opened by, as the page's chain names it
  categoryId: string;
  // The kind's own read of the chain; null while it loads or when it failed
  chain: ValueChain | null;
  // The kind as a root of the value chain
  root: ChainCardRef;
}

// Stated classes the head names; the rest are counted
const CLASSES_SHOWN = 3;

// The standings of a maker proven somewhere
const PROVEN = new Set(["proven", "proven_elsewhere"]);

interface StatProps {
  value: number;
  // The noun of one, and of many where it is not the noun of one with an "s"
  noun: string;
  plural?: string;
  accent?: boolean;
}

// One count of the head: the number large, the noun small
const Stat: React.FC<StatProps> = ({ value, noun, plural, accent = false }) => (
  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
    <Box
      component="span"
      sx={{
        ...monospaceStyle,
        fontSize: "1.125rem",
        fontWeight: 500,
        color: accent ? "accent.main" : "text.primary",
      }}
    >
      {value}
    </Box>{" "}
    <Typography component="span" variant="caption" color="text.secondary">
      {value === 1 ? noun : (plural ?? `${noun}s`)}
    </Typography>
  </Box>
);

/**
 * The head of the kind view: its backbone path ending in itself, its label,
 * the classes its products are stated as, its standing, nature and typical
 * host as chips, the counts with the source timeline and where it sits in the
 * chain the page shows, and, from another page, the step into the value
 * chain. "Make root" is in the header.
 */
const KindHead: React.FC<KindHeadProps> = ({
  category,
  categoryId,
  chain,
  root,
}) => {
  const page = usePageChain();
  // The path ends with the kind itself, which closes it unlinked
  const ancestors = category.path.filter((entry) => entry.id !== category.id);
  const name = category.label ?? UNLABELLED_CATEGORY;
  const classes = chain?.root.classes ?? [];
  const typicalHost = category.typical_hosts[0]?.category;
  const tags = [
    [category.curated ? "curated" : "stated", category.rank]
      .filter((part): part is string => Boolean(part))
      .join(" · "),
    ...(category.nature ? [codeWords(category.nature)] : []),
  ];
  const sourceDates = useMemo(
    () => category.sources.items.map((source) => source.date),
    [category.sources.items],
  );

  // The makers as the makers section has them; known once either chain has
  // the kind
  const onPage = cardOfCategory(page?.chain, categoryId);
  const known = Boolean(chain || onPage);
  const makers = chainMakers(chain, page?.chain, categoryId);
  const proven = makers.filter((maker) => PROVEN.has(maker.standing)).length;
  const articles = chain?.root.articles ?? onPage?.articles;
  const place = page?.chain
    ? `${chainPlaceLine(page.chain, categoryId)} ${ON_RECORD}`
    : ON_RECORD;

  return (
    <Box
      component="header"
      sx={{
        px: 2.5,
        pt: 2.5,
        pb: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      {ancestors.length > 0 && (
        <Box
          component="nav"
          aria-label="Backbone path"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            columnGap: 0.75,
            typography: "caption",
            color: "text.secondary",
          }}
        >
          {ancestors.map((entry) => (
            <React.Fragment key={entry.id}>
              <EntityLink id={entry.id} type={entry.type} name={entry.name} />
              <Box component="span" aria-hidden>
                ›
              </Box>
            </React.Fragment>
          ))}
          <Box component="span" aria-current="page" sx={{ color: "text.primary" }}>
            {name}
          </Box>
        </Box>
      )}
      <Box>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            overflowWrap: "anywhere",
            color: category.label ? "text.primary" : "text.secondary",
            fontStyle: category.label ? "normal" : "italic",
          }}
        >
          {name}
        </Typography>
        {category.other_labels.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {category.other_labels.join(" · ")}
          </Typography>
        )}
      </Box>

      {classes.length > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          role="group"
          aria-label="Stated classes"
        >
          {"Stated as "}
          {classes.slice(0, CLASSES_SHOWN).map((entry, index) => (
            <React.Fragment key={entry.category.id}>
              {index > 0 && ", "}
              <EntityLink
                id={entry.category.id}
                type="class"
                name={entry.category.name}
                sx={{ color: "text.primary" }}
              />
            </React.Fragment>
          ))}
          {classes.length > CLASSES_SHOWN && (
            <Box component="span" sx={{ color: "text.disabled" }}>
              {` +${classes.length - CLASSES_SHOWN}`}
            </Box>
          )}
          {" · each opens its own class"}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {tags.map((tag) => (
          <Chip key={tag} label={tag} />
        ))}
        {typicalHost && (
          <Chip
            sx={{
              position: "relative",
              "&:hover": { borderColor: "text.secondary" },
            }}
            label={
              <>
                {"typical host · "}
                {/* The link covers the whole chip */}
                <EntityLink
                  id={typicalHost.id}
                  type={typicalHost.type}
                  name={typicalHost.name}
                  sx={{
                    color: "inherit",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                    },
                  }}
                />
              </>
            }
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 2.25,
          rowGap: 0.75,
          px: 1.75,
          py: 1.5,
          backgroundColor: "background.raised",
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
        }}
      >
        <Box
          role="group"
          aria-label="Counts"
          sx={{
            display: "flex",
            alignItems: "baseline",
            flexWrap: "wrap",
            columnGap: 1.75,
          }}
        >
          <Stat value={category.products.total} noun="product" />
          {known && <Stat value={makers.length} noun="maker" />}
          {known && (
            <Stat value={proven} noun="proven" plural="proven" accent />
          )}
          {articles !== undefined && <Stat value={articles} noun="article" />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <SourceTimeline dates={sourceDates} />
        </Box>
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ flexBasis: "100%" }}
        >
          {place}
        </Typography>
      </Box>

      <ChainPlace root={root} categoryId={categoryId} />
    </Box>
  );
};

export default KindHead;
